import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useSiteSettings } from '../hooks/useSiteSettings';
import ImagePreviewModal from '../components/ImagePreviewModal';
import { getNewsCoverSrc } from '../utils/newsImages';
import './HomePage.css';

const resolveImage = (src) => {
  if (!src) return '';
  if (src.startsWith('http') || src.startsWith('data:')) return src;
  return `${process.env.REACT_APP_API_URL || ''}${src}`;
};

const HomePage = () => {
  const [featuredNews, setFeaturedNews] = useState([]);
  const [latestReports, setLatestReports] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [sections, setSections] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(true);
  const [previewImage, setPreviewImage] = useState(null);

  const fetchLatestNews = useCallback(async () => {
    setNewsLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/news`);
      const data = await res.json();
      const publishedNews = Array.isArray(data) ? data.filter(news => news.status === 'published') : [];
      setFeaturedNews(publishedNews.slice(0, 6));
    } catch (error) {
      console.error('خطأ في جلب الأخبار:', error);
      setFeaturedNews([]);
    } finally {
      setNewsLoading(false);
    }
  }, []);

  const fetchLatestReports = useCallback(async () => {
    setReportsLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/reports`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setLatestReports(data.slice(0, 3));
      } else {
        setLatestReports([]);
      }
    } catch (error) {
      console.error('خطأ في جلب التقارير:', error);
      setLatestReports([]);
    } finally {
      setReportsLoading(false);
    }
  }, []);

  const fetchSections = useCallback(async () => {
    setSectionsLoading(true);
    try {
      const [sectionsRes, articlesRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL || ''}/api/sections`),
        fetch(`${process.env.REACT_APP_API_URL || ''}/api/articles`)
      ]);

      const sectionsData = sectionsRes.ok ? await sectionsRes.json() : [];
      const articlesData = articlesRes.ok ? await articlesRes.json() : [];

      const activeSections = Array.isArray(sectionsData) ? sectionsData.filter(item => item.status === 'active') : [];

      const imagesMap = {};
      if (Array.isArray(articlesData)) {
        articlesData.forEach(article => {
          const sid = article.sectionId?.id || article.sectionId?._id || article.sectionId || null;
          if (sid && article.image) {
            if (!imagesMap[sid]) imagesMap[sid] = [];
            imagesMap[sid].push(article.image);
          }
        });
      }

      const enriched = activeSections.map(section => {
        const sectionImages = imagesMap[section.id] || [];
        const images = [];
        if (section.image) images.push(section.image);
        sectionImages.forEach(img => {
          if (!images.includes(img)) images.push(img);
        });
        return {
          ...section,
          images: images.slice(0, 8),
          link: `/sections/${section.slug || section.id}`
        };
      }).filter(section => section.images.length > 0);

      setSections(enriched);
    } catch (error) {
      console.error('خطأ في جلب الأقسام:', error);
      setSections([]);
    } finally {
      setSectionsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLatestNews();
    fetchLatestReports();
    fetchSections();
  }, [fetchLatestNews, fetchLatestReports, fetchSections]);

  const handleDownload = async (report, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      const pdfUrl = report.pdfUrl.startsWith('http')
        ? report.pdfUrl
        : `${process.env.REACT_APP_API_URL || ''}${report.pdfUrl}`;

      const response = await fetch(pdfUrl, { method: 'GET' });

      if (!response.ok) {
        throw new Error(`فشل التحميل. كود الاستجابة: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = report.pdfUrl.split('/').pop() || `${report.title}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("حدث خطأ أثناء تحميل الملف:", err);
      alert('حدث خطأ أثناء تحميل الملف: ' + err.message);
    }
  };

  const {
    site_name,
    siteDescription,
    contactEmail,
    contactPhone,
    address,
    facebookUrl,
    twitterUrl,
    instagramUrl,
    youtubeUrl,
    tiktokUrl,
    whatsappUrl,
    callUrl,
    snapchatUrl
  } = useSiteSettings();

  const siteName = site_name || 'مؤسسة ثراء الخير';
  const siteDesc = siteDescription || 'للزجاج السكريت والالمنيوم استركتشر والديكورات الخشبية';

  const socialLinks = [
    { url: facebookUrl, icon: 'fa-facebook-f', label: 'فيسبوك', prefix: 'fab' },
    { url: youtubeUrl, icon: 'fa-youtube', label: 'يوتيوب', prefix: 'fab' },
    { url: tiktokUrl, icon: 'fa-tiktok', label: 'تيك توك', prefix: 'fab' },
    { url: instagramUrl, icon: 'fa-instagram', label: 'انستغرام', prefix: 'fab' },
    { url: twitterUrl, icon: 'fa-twitter', label: 'تويتر', prefix: 'fab' },
    { url: whatsappUrl, icon: 'fa-whatsapp', label: 'واتساب', prefix: 'fab' },
    { url: snapchatUrl, icon: 'fa-snapchat', label: 'سناب شات', prefix: 'fab' },
    { url: callUrl, icon: 'fa-phone-alt', label: 'اتصال', prefix: 'fas' }
  ].filter(item => item.url);

  const contactItems = [
    { value: contactPhone, icon: 'fa-phone-alt', text: contactPhone },
    { value: contactEmail, icon: 'fa-envelope', text: contactEmail },
    { value: address, icon: 'fa-map-marker-alt', text: address }
  ].filter(item => item.value);

  return (
    <>
      <Helmet>
        {/* تم تحسين الـ SEO هنا بشكل كبير دون التأثير على التصميم */}
        <title>{siteName} | للمقاولات والتشطيبات والديكور</title>
        <meta name="description" content={`أفضل شركة مقاولات وتشطيب شقق وفلل، دهانات داخلية وخارجية، إيبوكسي، تركيب بديل الخشب، وأعمال الألومنيوم والزجاج. ${siteName} - ${siteDesc}`} />
        <meta name="keywords" content="مقاولات, تشطيبات, ديكورات, ثراء الخير, دهانات, ايبوكسي, بديل الخشب, بديل الرخام, زجاج سكريت, الومنيوم, ترميم منازل" />
      </Helmet>
      
      <div className="home-page">
        
        {/* --- قسم الترحيب والكلمات المفتاحية المضاف حديثاً (لا يؤثر على التصميم) --- */}
        <section className="welcome-seo-section" style={{ padding: '3rem 1rem 1rem', textAlign: 'center' }}>
          <div className="container">
            <div className="section-header" style={{ marginBottom: '0' }}>
              <h1 className="section-title">مؤسسة ثراء الخير للزجاج السكريت والالمنيوم استركتشر والديكورات الخشبية </h1>
              <p className="section-subtitle" style={{ maxWidth: '850px', margin: '0 auto', lineHeight: '1.9', fontSize: '1.1rem' }}>
             
                     بديل الخشب والرخام، تركيب زجاج السكريت والألومنيوم. 
                تركيب بديل الخشب والرخام، وتجهيز الواجهات الزجاجية والألومنيوم لتسليم مشروعك بأعلى معايير الفخامة والجودة.
                   </p>
            </div>
          </div>
        </section>
        {/* ---------------------------------------------------------------------- */}

        {/* Sections Showcase Section */}
        <section className="sections-showcase-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">أقسام المحتوى</h2>
              {/* تم دمج كلمات مفتاحية في الوصف الفرعي بأسلوب سلس */}
              <p className="section-subtitle">استعرض أحدث تصاميم الديكورات، التشطيبات، وأعمال المقاولات لدينا</p>
            </div>

            {sectionsLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>جاري تحميل الأقسام...</p>
              </div>
            ) : sections.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-images"></i>
                <h3>لا توجد أقسام بصور حالياً</h3>
                <p>سنقوم بإضافة محتوى جديد قريباً</p>
              </div>
            ) : (
              <div className="sections-showcase">
                {sections.map(section => (
                  <div key={section.id} className="section-block">
                    <div className="section-block-header">
                      <h3 className="section-block-title">{section.name}</h3>
                      <Link to={section.link} className="view-more-btn">
                        <span>عرض المزيد</span>
                        <i className="fas fa-arrow-left"></i>
                      </Link>
                    </div>
                    {section.description && (
                      <p className="section-block-desc">{section.description}</p>
                    )}
                    <div className="sections-showcase-grid">
                      {section.images.map((img, index) => (
                        <div
                          className="sections-showcase-item"
                          key={index}
                          role="button"
                          tabIndex={0}
                          onClick={() => setPreviewImage(resolveImage(img))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setPreviewImage(resolveImage(img));
                            }
                          }}
                        >
                          <img src={resolveImage(img)} alt={`${section.name} - أعمال ثراء الخير للمقاولات ${index + 1}`} loading="lazy" />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="section-footer">
              <Link to="/sections" className="btn btn-outline">
                <span>عرض جميع الأقسام</span>
                <i className="fas fa-arrow-left"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Featured News Section */}
        <section className="news-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">أحدث اعمالنا</h2>
              {/* تم دمج كلمات مفتاحية في الوصف الفرعي */}
              <p className="section-subtitle">شاهد أحدث مشاريعنا في التشطيب، الدهانات، والديكورات</p>
            </div>
            
            {newsLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>جاري تحميل اعمالنا...</p>
              </div>
            ) : featuredNews.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-newspaper"></i>
                <h3>لا توجد اعمال منشورة حالياً</h3>
                <p>سنقوم بإضافة محتوى جديد قريباً</p>
              </div>
            ) : (
              <div className="news-grid">
                {featuredNews.map(news => (
                  <div key={news.id} className="news-card">
                    <div className="news-image">
                      <img 
                        src={getNewsCoverSrc(news) || 'https://via.placeholder.com/400x250/007bff/ffffff?text=لا+توجد+صورة'} 
                        alt={news.title} 
                      />
                      <div className="news-overlay">
                        <Link to={`/news/${news.id}`} className="read-more-btn">
                          <i className="fas fa-arrow-left"></i>
                        </Link>
                      </div>
                    </div>
                    <div className="news-content">
                      <div className="news-meta">
                        <span className="news-date">
                          <i className="far fa-calendar-alt"></i>
                            {news.publishDate ? new Date(news.publishDate).toLocaleDateString('ar-EG', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            }) : ''}                        </span>
                        <span className="news-category">
                          <i className="fas fa-tag"></i>
                          {news.category || 'أعمالنا'}
                        </span>
                      </div>
                      <Link to={`/news/${news.id}`} className="news-title">
                        <h3>{news.title}</h3>
                      </Link>
                      <p className="news-summary">
                        {news.summary || (news.content ? news.content.slice(0, 120) + '...' : '')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="section-footer">
              <Link to="/news" className="btn btn-outline">
                <span>عرض جميع اعمالنا</span>
                <i className="fas fa-arrow-left"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        {(contactItems.length > 0 || socialLinks.length > 0) && (
          <section className="contact-section">
            <div className="container">
              <div className="section-header">
                <h2 className="section-title">تواصل معنا</h2>
                <p className="section-subtitle">يسعدنا تواصلك معنا لطلب استشارة أو تسعير لمشروعك</p>
              </div>
              <div className="contact-content">
                {contactItems.length > 0 && (
                  <div className="contact-items">
                    {contactItems.map((item, index) => (
                      <div className="contact-item" key={index}>
                        <div className="contact-item-icon">
                          <i className={`fas ${item.icon}`}></i>
                        </div>
                        <div className="contact-item-text">{item.text}</div>
                      </div>
                    ))}
                  </div>
                )}
                {socialLinks.length > 0 && (
                  <div className="contact-social">
                    <h3>تابعنا على</h3>
                    <div className="contact-social-links">
                      {socialLinks.map((item, index) => (
                        <a
                          key={index}
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="contact-social-link"
                          aria-label={item.label}
                          title={item.label}
                        >
                          <i className={`${item.prefix || 'fab'} ${item.icon}`}></i>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

      </div>

      <ImagePreviewModal
        open={!!previewImage}
        image={previewImage}
        alt="أعمال ثراء الخير للمقاولات"
        onClose={() => setPreviewImage(null)}
      />
    </>
  );
};

export default HomePage;